import React from "react";
import "./Bio.css";
import MainLayout from "../../components/MainLayout/MainLayout";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faCode,
  faLaptopCode,
  faUsers,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useInView } from "react-intersection-observer";

const Bio = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // Intersection observer hooks for scroll animations
  const [bioHeaderRef, bioHeaderInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [bioContentRef, bioContentInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [strengthsRef, strengthsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <MainLayout>
      <motion.div
        className="bio-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <section className="bio-header-section" ref={bioHeaderRef}>
          <div className="bio-container">
            <motion.div
              className="bio-header"
              initial={{ opacity: 0, y: 30 }}
              animate={
                bioHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
              }
              transition={{ duration: 0.8 }}
            >
              <h1 className="bio-title">Biography</h1>
              <div className="bio-title-underline"></div>
              <motion.p
                className="bio-intro"
                initial={{ opacity: 0 }}
                animate={bioHeaderInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Hello! I'm <span className="bio-highlight">Moeez Abdul</span> –
                an{" "}
                <span className="bio-highlight">
                  Technical Consultant
                </span>{" "}
                and <span className="bio-highlight">Software Engineer</span>.
              </motion.p>
            </motion.div>
          </div>
        </section>

        <section className="bio-content-section" ref={bioContentRef}>
          <div className="bio-container">
            <motion.div
              className="bio-content"
              variants={containerVariants}
              initial="hidden"
              animate={bioContentInView ? "visible" : "hidden"}
            >
              <motion.div className="bio-card" variants={itemVariants}>
                <div className="bio-card-icon">
                  <FontAwesomeIcon icon={faCode} />
                </div>
                <div className="bio-card-content">
                  <p className="bio-text">
                    🚀 I'm passionate about transforming complex challenges into
                    innovative, tech-driven solutions that streamline operations
                    and deliver impactful results. With a BSc in Software
                    Development (Hons) from The Manchester Metropolitan
                    University and my role as a Technical Consultant
                    at{" "}
                    <a href="https://theconfigteam.com/" className="bio-link">
                      The Config Team
                    </a>
                    , I've built a foundation that blends cutting-edge SAP
                    solutions with my full-stack development expertise.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="bio-strengths-section" ref={strengthsRef}>
          <div className="bio-container">
            <motion.h2
              className="bio-section-title"
              initial={{ opacity: 0, y: 20 }}
              animate={
                strengthsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.6 }}
            >
              <FontAwesomeIcon icon={faStar} className="bio-section-icon" />
              What Sets Me Apart
            </motion.h2>

            <motion.div
              className="bio-strengths"
              variants={containerVariants}
              initial="hidden"
              animate={strengthsInView ? "visible" : "hidden"}
            >
              <motion.div className="bio-strength-card" variants={itemVariants}>
                <div className="bio-strength-icon">
                  <FontAwesomeIcon icon={faLaptopCode} />
                </div>
                <h3>Technical Mastery</h3>
                <p>
                  Proficient in JavaScript, C#, ASP.NET, MERN stack, and SAP
                  technologies like ABAP and SAP ECC/S4HANA, I craft scalable
                  systems that optimise supply chains and boost productivity.
                </p>
              </motion.div>

              <motion.div className="bio-strength-card" variants={itemVariants}>
                <div className="bio-strength-icon">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <h3>Business-Centric Solutions</h3>
                <p>
                  I don't just build software; I deliver solutions that drive
                  efficiency, align with business objectives, and exceed client
                  expectations.
                </p>
              </motion.div>

              <motion.div className="bio-strength-card" variants={itemVariants}>
                <div className="bio-strength-icon">
                  <FontAwesomeIcon icon={faCode} />
                </div>
                <h3>Full-Stack Powerhouse</h3>
                <p>
                  From AI-driven projects like Convo-GPT to enterprise-level
                  systems, I bring a holistic, agile approach to every project.
                </p>
              </motion.div>

              <motion.div className="bio-strength-card" variants={itemVariants}>
                <div className="bio-strength-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3>Collaboration & Leadership</h3>
                <p>
                  Whether it's leading technical workshops or managing key
                  stakeholder relationships, I believe in clear communication
                  and teamwork to drive results.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="bio-cta-section">
          <div className="bio-container">
            <motion.div
              className="bio-cta-container"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bio-cta-content">
                <h2>Ready to Innovate Together?</h2>
                <p>
                  I combine a relentless drive for innovation with a deep
                  understanding of business processes. If you're looking for
                  someone who can optimise your systems, streamline operations,
                  and bring fresh, tech-driven ideas to your projects – let's
                  connect!
                </p>
                <div className="bio-cta-buttons">
                  <motion.a
                    href="/Portfolio"
                    className="bio-primary-btn"
                    whileHover={{
                      y: -5,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    View My Projects <FontAwesomeIcon icon={faArrowRight} />
                  </motion.a>
                  <motion.a
                    href="/CV"
                    className="bio-secondary-btn"
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    See My Experience <FontAwesomeIcon icon={faArrowRight} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </MainLayout>
  );
};

export default Bio;
